// Keeps a prerendered page on screen until the app has real content.
//
// createRoot() replaces #root's children on its first commit. The route
// components are lazy, so that first commit is usually the Suspense fallback:
// visitors saw the prerendered page, then "Loading page...", then the same
// page again. Moving the prerendered nodes into a sibling keeps them visible
// while React mounts into a hidden #root, and the swap happens in the commit
// that replaces the fallback, before the browser paints.

const SAFETY_TIMEOUT_MS = 15000;
const HIDDEN_ROOT_STYLES = ['position', 'top', 'left', 'right', 'visibility', 'pointer-events'];

let snapshot = null;
let rootElement = null;
let pendingFallbacks = 0;
let safetyTimer = null;

export const releasePrerenderedPage = () => {
  if (!snapshot) return;
  window.clearTimeout(safetyTimer);
  snapshot.remove();
  snapshot = null;
  HIDDEN_ROOT_STYLES.forEach((name) => rootElement.style.removeProperty(name));
  rootElement.removeAttribute('aria-hidden');
};

export const holdPrerenderedPage = (root) => {
  if (!root || !root.firstElementChild) return;
  if (typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap') return;

  rootElement = root;
  snapshot = document.createElement('div');
  snapshot.setAttribute('data-mediasfu-prerender-snapshot', '');
  while (root.firstChild) snapshot.appendChild(root.firstChild);
  root.parentNode.insertBefore(snapshot, root);

  // Out of flow but still laid out, so mount-time measurements stay real.
  Object.assign(root.style, {
    position: 'absolute',
    top: '0',
    left: '0',
    right: '0',
    visibility: 'hidden',
    pointerEvents: 'none',
  });
  root.setAttribute('aria-hidden', 'true');
  safetyTimer = window.setTimeout(releasePrerenderedPage, SAFETY_TIMEOUT_MS);
};

// Called from the route Suspense fallback's layout effect. Returns the cleanup.
export const trackRouteFallback = () => {
  if (!snapshot) return undefined;
  pendingFallbacks += 1;
  return () => {
    pendingFallbacks -= 1;
    // Checked after the commit, not inside it. When the route resolves, its
    // fallback unmounts in the same commit that mounts the page, and the
    // page's own section fallbacks register only after this cleanup has run.
    // Releasing here showed the page with its hero still a loader. A
    // microtask runs once the commit's layout effects are done and still
    // before the browser paints.
    queueMicrotask(() => {
      if (pendingFallbacks <= 0) releasePrerenderedPage();
    });
  };
};

// Called once the app's first commit has run its layout effects.
export const noteAppCommitted = () => {
  if (pendingFallbacks <= 0) releasePrerenderedPage();
};
